/**
 * 表格提取导出服务
 * 从 Markdown 内容中提取表格数据并转换为 CSV 和 Excel 格式
 */

import * as XLSX from 'xlsx'
import { ExportOptions } from '../../../types/export'
import { downloadBlob } from '../../../utils/download'
import {
  extractTablesFromMarkdown,
  convertTablesToCSV,
  convertTablesToExcel,
  getTableStatistics
} from './tableExtractor'
import { generateExportFilename } from './exportStyles'

/**
 * 提取表格并导出为 CSV
 */
export const exportToCSV = async (
  content: string,
  options: ExportOptions
): Promise<void> => {
  try {
    // 提取表格数据
    const tables = extractTablesFromMarkdown(content)
    
    if (tables.length === 0) {
      throw new Error('文档中没有找到表格数据')
    }

    // 检查是否包含有效的表格（有表头和至少一行数据）
    const validTables = tables.filter(table => 
      table.headers.length > 0 && table.rows.length > 0
    )
    
    if (validTables.length === 0) {
      throw new Error('没有找到有效的表格数据（需要包含表头和数据行）')
    }

    // 转换为 CSV 格式
    const csvContent = convertTablesToCSV(validTables, {
      includeTitles: true,
      separator: ','
    })

    // 创建 blob
    const blob = new Blob(['\ufeff' + csvContent], { 
      type: 'text/csv;charset=utf-8' 
    })

    // 生成文件名
    const filename = generateExportFilename('csv', options.filename)
    downloadBlob(blob, filename)

  } catch (error) {
    console.error('CSV 表格提取失败:', error)
    throw new Error(`CSV 表格提取失败: ${error instanceof Error ? error.message : '未知错误'}`)
  }
}

/**
 * 提取表格并导出为 Excel
 */
export const exportToExcel = async (
  content: string,
  options: ExportOptions
): Promise<void> => {
  try {
    // 提取表格数据
    const tables = extractTablesFromMarkdown(content)
    
    if (tables.length === 0) {
      throw new Error('文档中没有找到表格数据')
    }

    // 检查是否包含有效的表格
    const validTables = tables.filter(table => 
      table.headers.length > 0 && table.rows.length > 0
    )
    
    if (validTables.length === 0) {
      throw new Error('没有找到有效的表格数据（需要包含表头和数据行）')
    }

    // 创建工作簿
    const workbook = XLSX.utils.book_new()

    // 为每个表格创建一个工作表
    validTables.forEach((table, index) => {
      const worksheets = convertTablesToExcel([table], {
        includeTitles: true
      })
      
      if (worksheets.length > 0) {
        const worksheet = XLSX.utils.aoa_to_sheet(worksheets[0])
        
        // 设置工作表名称（Excel 工作表名称限制）
        const sheetName = table.title.length > 31 
          ? `表格${index + 1}` 
          : table.title.replace(/[\\/:*?"<>|]/g, '_').slice(0, 31)
        
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)
      }
    })

    // 添加统计信息工作表
    const statistics = getTableStatistics(validTables)
    const statsData = [
      ['表格统计信息'],
      [''],
      ['总表格数', statistics.totalTables],
      ['总行数', statistics.totalRows],
      ['最大列数', statistics.totalColumns],
      [''],
      ['详细统计'],
      ['表格名称', '行数', '列数']
    ]
    
    statistics.tableDetails.forEach(detail => {
      statsData.push([detail.title, detail.rows, detail.columns])
    })
    
    const statsWorksheet = XLSX.utils.aoa_to_sheet(statsData)
    XLSX.utils.book_append_sheet(workbook, statsWorksheet, '统计信息')

    // 生成 Excel 文件
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
    const blob = new Blob([excelBuffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    })

    // 生成文件名
    const filename = generateExportFilename('xlsx', options.filename)
    downloadBlob(blob, filename)

  } catch (error) {
    console.error('Excel 表格提取失败:', error)
    throw new Error(`Excel 表格提取失败: ${error instanceof Error ? error.message : '未知错误'}`)
  }
}

/**
 * 预览表格提取结果（返回 CSV 文本）
 */
export const previewTableExtraction = (
  content: string,
  _options: ExportOptions
): string => {
  try {
    const tables = extractTablesFromMarkdown(content)
    
    if (tables.length === 0) {
      throw new Error('文档中没有找到表格数据')
    }

    const validTables = tables.filter(table => 
      table.headers.length > 0 && table.rows.length > 0
    )
    
    if (validTables.length === 0) {
      throw new Error('没有找到有效的表格数据')
    }

    return convertTablesToCSV(validTables, {
      includeTitles: true,
      separator: ','
    })

  } catch (error) {
    console.error('表格提取预览失败:', error)
    throw new Error(`表格提取预览失败: ${error instanceof Error ? error.message : '未知错误'}`)
  }
}

/**
 * 获取表格提取预览信息
 */
export const getTableExtractionInfo = (content: string): {
  tableCount: number
  totalRows: number
  totalColumns: number
  hasTables: boolean
  message: string
} => {
  const tables = extractTablesFromMarkdown(content)
  const validTables = tables.filter(table => 
    table.headers.length > 0 && table.rows.length > 0
  )
  
  if (validTables.length === 0) {
    if (tables.length === 0) {
      return {
        tableCount: 0,
        totalRows: 0,
        totalColumns: 0,
        hasTables: false,
        message: '文档中没有找到表格数据'
      }
    } else {
      return {
        tableCount: tables.length,
        totalRows: 0,
        totalColumns: 0,
        hasTables: false,
        message: '找到了表格但没有有效的表头或数据行'
      }
    }
  }
  
  const statistics = getTableStatistics(validTables)
  
  return {
    tableCount: validTables.length,
    totalRows: statistics.totalRows,
    totalColumns: statistics.totalColumns,
    hasTables: true,
    message: `找到 ${validTables.length} 个有效表格，共 ${statistics.totalRows} 行数据`
  }
}

/**
 * 检查表格提取支持
 */
export const checkTableExtractionSupport = (): boolean => {
  return typeof window !== 'undefined' && 
         typeof document !== 'undefined' && 
         typeof Blob !== 'undefined' &&
         typeof URL !== 'undefined'
}